/**
 * pages/Dashboard.tsx — the list of scheduled updates for the business unit this window is on.
 *
 * The screen answers four questions and is arranged around them: what is scheduled, what does it watch,
 * where does it go, and did the last one work. Everything else is a detail on another screen.
 *
 * **The fan-out is deliberate and is the honest cost of the current API.** `GET /api/jobs` returns the job
 * rows only — repositories, destinations and the last run each live behind their own scoped route, because
 * each is separately permission-checked on the server. So a row of this table is assembled from three
 * calls, in parallel, per job. That is fine at the scale this list is read at (a business unit runs a
 * handful of updates) and would be wrong at a hundred: the fix then is an expanded list route on the
 * server, not a client that fetches less than it shows. Saying so here is cheaper than rediscovering it.
 *
 * A failed side-call **does not fail the row**. A job whose repositories could not be read is still a job
 * that exists and is still due at a time the reader needs to know; showing nothing because one of three
 * calls refused would hide the schedule to protect a cell. The cell says it could not be read instead.
 *
 * The empty state names the next action rather than the absence. "No jobs" tells a first-time user that
 * the screen loaded; it does not tell them that an update needs repositories and a destination, or that
 * creating one requires the authoring role they may not hold.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, api, type Job, type JobListResponse, type Run, type RunListResponse } from "../lib/api";
import {
  cadenceHours,
  formatCadence,
  formatCount,
  formatDateTime,
  formatDuration,
  formatRelative,
  humanise,
  updateKindHint,
  updateKindLabel,
} from "../lib/format";
import { startManualRun } from "../lib/manualRun";
import {
  Badge,
  Button,
  DeniedState,
  ErrorState,
  LoadingState,
  Page,
  Spinner,
  Table,
  useToast,
  type Column,
  type Tone,
} from "../components/ui";

/* ---------------------------------------------------------------------------------------------------
 * The shapes this screen reads, transcribed from `server/routes/jobs.ts`'s `repoView`/`targetView`.
 * ------------------------------------------------------------------------------------------------- */

export interface JobRepoView {
  readonly id: string;
  readonly jobId: string;
  readonly repoPath: string;
  readonly branch: string | null;
}

export interface JobTargetView {
  readonly id: string;
  readonly jobId: string;
  readonly kind: "confluence" | "slack";
  readonly spaceKey: string | null;
  readonly pageId: string | null;
  readonly pageTitle: string | null;
  readonly channelId: string | null;
  readonly loadBearing: boolean;
  readonly enabled: boolean;
}

interface JobDetailResponse {
  readonly job: Job;
  readonly repos: readonly JobRepoView[];
  readonly targets: readonly JobTargetView[];
}

/** One row: the job, plus the three things the server keeps behind their own routes. */
export interface JobSummary {
  readonly job: Job;
  /** `null` means the call refused or failed — distinct from an empty list, which means "none chosen". */
  readonly repos: readonly JobRepoView[] | null;
  readonly targets: readonly JobTargetView[] | null;
  readonly lastRun: Run | null;
}

/* ---------------------------------------------------------------------------------------------------
 * Loading
 * ------------------------------------------------------------------------------------------------- */

/**
 * The three failure shapes a screen must keep apart: denied, failed, and simply not here yet.
 *
 * Held locally rather than shared because the shell's own version lives in `App.tsx` and is private to it;
 * duplicating fifteen lines is preferable to a page reaching into the application module for a hook.
 */
interface Async<T> {
  readonly data: T | null;
  readonly error: ApiError | null;
  readonly loading: boolean;
}

function useAsync<T>(
  load: (signal: AbortSignal) => Promise<T>,
  key: string,
): Async<T> & { readonly reload: () => void; readonly attempt: number } {
  const [state, setState] = useState<Async<T>>({ data: null, error: null, loading: true });
  const [attempt, setAttempt] = useState(0);

  /**
   * Clearing belongs to a change of subject, not to a re-read.
   *
   * The loader below runs again on every poll, and when it blanked the state first the whole table
   * vanished and returned every three seconds while a run was in flight — precisely when somebody is
   * watching it, and taking the sort they had chosen with it each time. So the reset lives here, keyed on
   * the request rather than on the attempt, and a refresh leaves the previous rows on screen until the new
   * ones land. `RunHistory` already worked this way; this screen simply never did.
   */
  useEffect(() => {
    setState({ data: null, error: null, loading: true });
  }, [key]);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    load(controller.signal)
      .then((data) => {
        if (live) setState({ data, error: null, loading: false });
      })
      .catch((cause: unknown) => {
        if (!live) return;
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        const error = cause instanceof ApiError ? cause : new ApiError(0, "internal_error", String(cause), null);
        // A poll that fails keeps the rows it already has and says so above them: a momentary blip is not
        // a reason to take the list away, and doing so would reintroduce the flashing by another route.
        // A REFUSAL is the exception — the reader has just been told they may not see this, so it goes.
        setState((previous) => ({
          data: error.isPermissionDenied ? null : previous.data,
          error,
          loading: false,
        }));
      });

    return () => {
      live = false;
      controller.abort();
    };
    // `load` is re-created every render; `key` is what says the request actually changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, attempt]);

  const reload = useCallback((): void => setAttempt((current) => current + 1), []);
  return { ...state, reload, attempt };
}

/** A side-call whose refusal is a cell's problem, never the row's. Resolves to null rather than throwing. */
async function optional<T>(work: Promise<T>): Promise<T | null> {
  try {
    return await work;
  } catch {
    return null;
  }
}

/** The default loader: the job list, then the three per-job reads, in parallel. */
export async function loadJobSummaries(signal: AbortSignal): Promise<readonly JobSummary[]> {
  const list = await api.get<JobListResponse>("/api/jobs", { signal });

  return Promise.all(
    list.jobs.map(async (job): Promise<JobSummary> => {
      const path = `/api/jobs/${encodeURIComponent(job.id)}`;
      const [detail, runs] = await Promise.all([
        optional(api.get<JobDetailResponse>(path, { signal })),
        optional(api.get<RunListResponse>(`${path}/runs`, { query: { limit: 1 }, signal })),
      ]);
      return {
        job,
        repos: detail === null ? null : detail.repos,
        targets: detail === null ? null : detail.targets,
        // The route answers newest-first; the most recent run is the head of the list or nothing at all.
        lastRun: runs === null ? null : (runs.runs[0] ?? null),
      };
    }),
  );
}

/* ---------------------------------------------------------------------------------------------------
 * Rendering
 * ------------------------------------------------------------------------------------------------- */

const RUN_TONE: Readonly<Record<string, Tone>> = {
  succeeded: "success",
  running: "info",
  skipped: "neutral",
  failed: "danger",
};

/** Where an update goes, said the way a reader thinks of it rather than the way the row is stored. */
export function describeTarget(target: JobTargetView): string {
  if (target.kind === "confluence") {
    return `Confluence ${target.spaceKey ?? "?"} / ${target.pageTitle ?? "?"}`;
  }
  return `Slack ${target.channelId ?? "?"}`;
}

/** "Never" and "could not be read" are different answers, and a dash for both would merge them. */
function countCell(items: readonly unknown[] | null): string {
  return items === null ? "Unavailable" : formatCount(items.length);
}

export interface DashboardProps {
  readonly buId: string;
  /** Drawn from `/api/me`'s roles. A hidden button is UX — the server re-checks the role on the write. */
  readonly canAuthor?: boolean;
  readonly onCreate?: () => void;
  readonly onEdit?: (jobId: string) => void;
  /** Injected by tests. Production passes nothing and gets the fan-out above. */
  readonly load?: (signal: AbortSignal) => Promise<readonly JobSummary[]>;
  /** Test seams for publishing on demand: the request itself, and how often the list is re-read. */
  readonly onStartRun?: (jobId: string) => Promise<unknown>;
  readonly pollMs?: number;
}

/** How often the list is re-read while something is publishing. An idle list never polls. */
const POLL_MS = 3000;

export function Dashboard({
  buId,
  canAuthor = false,
  onCreate,
  onEdit,
  load,
  onStartRun = startManualRun,
  pollMs = POLL_MS,
}: DashboardProps): JSX.Element {
  const toast = useToast();
  const loader = load ?? loadJobSummaries;
  const summaries = useAsync((signal) => loader(signal), `dashboard:${buId}`);
  const [starting, setStarting] = useState<string | null>(null);

  const rows = summaries.data;
  const publishing = (rows ?? []).filter((row) => row.lastRun?.status === "running");

  /**
   * Watch what is in flight, from the server.
   *
   * A row is publishing when its own latest run says so, which means this survives a refresh, a different
   * tab and a different person — the progress was never held here to be lost. The list is re-read while
   * anything is running and not otherwise, so an idle business unit costs exactly one request.
   *
   * **`attempt` is load-bearing in the dependencies**, and is why it is returned at all. It is what arms
   * the next timer after a poll lands. Before the rows persisted across a re-read, `publishing.length`
   * fell to zero and back on every cycle and re-armed it as a side effect of the very blanking this screen
   * has stopped doing; without `attempt`, polling would now stop dead after a single tick.
   */
  useEffect(() => {
    if (publishing.length === 0 && starting === null) return;
    const timer = setTimeout(summaries.reload, pollMs);
    return () => clearTimeout(timer);
  }, [publishing.length, starting, summaries.attempt, summaries.reload, pollMs]);

  // Every run this screen has seen in flight; the set is what turns "finished" into "just finished here".
  const watched = useRef<Set<string>>(new Set());
  for (const row of publishing) if (row.lastRun) watched.current.add(row.lastRun.id);

  // A run that was in flight and no longer is, reported once. `finished` is keyed on the run id, so a
  // second render cannot announce the same landing twice.
  const announced = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (rows === null) return;
    for (const row of rows) {
      const run = row.lastRun;
      if (run === null || run.status === "running") continue;
      if (!watched.current.has(run.id) || announced.current.has(run.id)) continue;
      announced.current.add(run.id);
      toast.show(
        run.status === "failed"
          ? `“${row.job.name}” failed`
          : run.status === "skipped"
            ? `“${row.job.name}”: nothing to publish`
            : `“${row.job.name}” published`,
        {
          tone: run.status === "failed" ? "danger" : "info",
          detail:
            run.status === "succeeded"
              ? `${run.counters.itemsPublished} item(s) in ${formatDuration(run.startedAt, run.finishedAt)}. Open it for the detail.`
              : (run.error ?? "Open the run history for what it reviewed."),
        },
      );
    }
  }, [rows, toast]);

  const startRun = (row: JobSummary): void => {
    setStarting(row.job.id);
    void Promise.resolve(onStartRun(row.job.id))
      .catch((cause: unknown) => {
        const failure =
          cause instanceof ApiError ? cause : new ApiError(0, "internal_error", String(cause), null);
        toast.show(
          failure.status === 503 ? "Publishing on demand is not configured" : "The update could not be started",
          { tone: failure.status === 503 ? "info" : "danger", detail: failure.userMessage },
        );
      })
      .finally(() => {
        setStarting(null);
        summaries.reload();
      });
    summaries.reload();
  };

  const createButton =
    canAuthor && onCreate !== undefined ? (
      <Button variant="primary" onClick={onCreate}>
        New update
      </Button>
    ) : undefined;

  const columns: readonly Column<JobSummary>[] = [
    {
      key: "name",
      header: "Update",
      type: "text",
      value: (row) => row.job.name,
      render: (row) => <a href={`#/jobs/${encodeURIComponent(row.job.id)}/runs`}>{row.job.name}</a>,
    },
    {
      /*
       * WHAT each update says, beside WHERE it goes and WHEN — because with three kinds in one list the
       * name is no longer enough to tell them apart, and two rows watching the same repository can now be
       * two entirely different products.
       *
       * SORTABLE, like every other value column here. `sortable: false` has four named exemptions —
       * avatars, actions, selection and free-form icons — and a column of three words is none of them;
       * grouping a unit's changelogs apart from its document updates is exactly the comparison a reader
       * of a mixed list makes. It sorts on the LABEL rather than on the wire value so that the order on
       * screen is the order of the words on screen, which `executive_summary` before `changelog` would
       * not be.
       */
      key: "updateType",
      header: "What it says",
      type: "text",
      value: (row) => updateKindLabel(row.job.kind),
      render: (row) => <span title={updateKindHint(row.job.kind)}>{updateKindLabel(row.job.kind)}</span>,
    },
    {
      key: "enabled",
      header: "State",
      type: "boolean",
      value: (row) => row.job.enabled,
      // An orphaned update outranks "enabled" in the state column, because it IS the state: the schedule
      // still says it should publish, and it will not, until somebody reassigns it. Left as a bare
      // "Enabled" badge, the page it maintains would go stale with nothing on this screen to say why.
      render: (row) =>
        row.job.owner.orphaned ? (
          <span
            title={
              row.job.owner.email === null
                ? "The person this update runs as no longer exists. A business-unit administrator must reassign it."
                : `${row.job.owner.email} is ${row.job.owner.status ?? "unavailable"}, so this update cannot publish. A business-unit administrator must reassign it.`
            }
          >
            <Badge tone="danger">Needs reassigning</Badge>
          </span>
        ) : (
          <Badge tone={row.job.enabled ? "success" : "neutral"}>
            {row.job.enabled ? "Enabled" : "Paused"}
          </Badge>
        ),
    },
    {
      key: "cadence",
      header: "How often",
      // Sorted by the actual gap between updates rather than by the cadence word, so the column orders
      // from most frequent to least — which is the comparison a reader of this column is making.
      type: "number",
      value: (row) => cadenceHours(row.job),
      render: (row) => formatCadence(row.job),
    },
    {
      key: "repos",
      header: "Repositories",
      type: "number",
      numeric: true,
      // Sorted on the count, listed in the title: a cell wide enough for nine repository paths is a cell
      // that has stopped being scannable, and the number is what a reader compares between rows.
      value: (row) => (row.repos === null ? null : row.repos.length),
      render: (row) => (
        <span title={row.repos === null ? "This job's repositories could not be read" : row.repos.map((repo) => repo.repoPath).join("\n")}>
          {countCell(row.repos)}
        </span>
      ),
    },
    {
      key: "targets",
      header: "Destinations",
      type: "text",
      value: (row) => (row.targets === null ? null : row.targets.map(describeTarget).join(", ")),
      render: (row) => {
        if (row.targets === null) return "Unavailable";
        if (row.targets.length === 0) return "None — nothing is published";
        return (
          <span title={row.targets.map(describeTarget).join("\n")}>
            {row.targets.map(describeTarget).join(", ")}
          </span>
        );
      },
    },
    {
      key: "lastRun",
      header: "Last run",
      type: "text",
      value: (row) => row.lastRun?.status ?? null,
      render: (row) => {
        const run = row.lastRun;
        if (run === null) return "Not yet run";
        // A run in flight is the row's most important fact, so it gets the spinner and the counters rather
        // than a badge reading "Running" that could equally mean "wedged since Tuesday". The numbers come
        // from the server's own row and climb as it works.
        if (run.status === "running") {
          // The figures are the run row's own and climb as it works — but three of the four count
          // COMMITS, and a document update never looks at one. Left as they were, a healthy executive
          // summary spent its whole run reporting "1 repo(s) · 0 commit(s)" beside a spinner, which reads
          // as a publish that has found nothing. Same row, same source, counted by what the kind counts.
          const isDocument = row.job.kind !== "changelog";
          return (
            <span className="row">
              <Spinner label="Publishing" />
              <Badge tone="info">Publishing now</Badge>
              <span
                className="field__hint"
                title={
                  isDocument
                    ? `${formatCount(run.counters.itemsPublished)} page section(s) composed so far — nothing is delivered until the run ends`
                    : `${formatCount(run.counters.commitsSeen)} commits seen, ${formatCount(run.counters.commitsDropped)} dropped automatically, ${formatCount(run.counters.commitsRoutine)} read and not reported, ${formatCount(run.counters.itemsPublished)} items written so far — nothing is delivered until the run ends`
                }
              >
                {isDocument ? (
                  <>{formatCount(run.counters.itemsPublished)} section(s)</>
                ) : (
                  <>
                    {formatCount(run.counters.reposCovered)} repo(s) ·{" "}
                    {formatCount(run.counters.commitsSeen)} commit(s)
                  </>
                )}
              </span>
            </span>
          );
        }
        return (
          <span className="row">
            <Badge tone={RUN_TONE[run.status] ?? "neutral"}>{humanise(run.status)}</Badge>
            <time title={formatDateTime(run.startedAt)}>{formatRelative(run.startedAt)}</time>
          </span>
        );
      },
    },
    {
      key: "nextDueAt",
      header: "Next due",
      type: "date",
      value: (row) => row.job.nextDueAt,
      render: (row) =>
        row.job.nextDueAt === null ? (
          "Unscheduled"
        ) : (
          <time title={formatDateTime(row.job.nextDueAt)}>{formatRelative(row.job.nextDueAt)}</time>
        ),
    },
    {
      key: "actions",
      actions: true,
      header: "Actions",
      // One of the four permitted unsortable exemptions: an action column has no value to order by.
      sortable: false,
      render: (row) => {
        const running = row.lastRun?.status === "running";
        return (
          <span className="row">
            {canAuthor ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => startRun(row)}
                loading={starting === row.job.id}
                disabled={running || starting !== null}
                title={
                  running
                    ? "This update is publishing now"
                    : row.job.owner.orphaned
                      ? "This update has no usable owner, so it would publish nothing until it is reassigned"
                      : "Fetch commits, summarise them and publish, without waiting for the schedule"
                }
              >
                {running ? "Publishing…" : "Update now"}
              </Button>
            ) : null}
            <Button variant="link" size="sm" onClick={() => (window.location.hash = `#/jobs/${encodeURIComponent(row.job.id)}/runs`)}>
              History
            </Button>
            {onEdit === undefined ? null : (
              <Button variant="link" size="sm" onClick={() => onEdit(row.job.id)}>
                Edit
              </Button>
            )}
          </span>
        );
      },
    },
  ];

  return (
    <Page
      title="Automations"
      // The lede named the changelog as though it were the whole product, which it stopped being when the
      // document kinds arrived: a reader of a list holding all three would be told the screen is about
      // one of them. What has NOT changed is the sentence that matters — the wording is fixed whichever
      // kind an update is, and that is still the point rather than a shortcoming.
      //
      // ⚠️ Cut from four lines to one on the owner's instruction of 10 August 2026, and the clause above is
      // what decided WHICH words go. The middle enumerated all three kinds, which is what fixed the earlier
      // defect — but the list below states each row's kind in its own column, so the enumeration was the
      // screen explaining itself twice. Naming NONE of them cannot reintroduce naming only one. The fixed
      // wording is the half that has nowhere else to appear, so it is the half that stays.
      lede="Each update runs on a cadence and publishes one standardized page. How it reads is not configurable — that is the point."
      {...(createButton === undefined ? {} : { actions: createButton })}
    >
      {summaries.loading ? <LoadingState label="Loading automations" /> : null}

      {summaries.error !== null ? (
        summaries.error.isPermissionDenied ? (
          <DeniedState body={summaries.error.userMessage} />
        ) : (
          <ErrorState
            message={summaries.error.userMessage}
            traceId={summaries.error.traceId}
            onRetry={summaries.reload}
          />
        )
      ) : null}

      {summaries.data !== null ? (
        <Table
          caption="Automations"
          columns={columns}
          rows={summaries.data}
          rowKey={(row) => row.job.id}
          defaultSort={{ key: "nextDueAt", direction: "ascending" }}
          titleKey="name"
          mobile="cards"
          empty={{
            title: "No automations are scheduled in this business unit yet",
            // Four choices now, not three: what an update SAYS is the first of them, and an empty state
            // that still described only the changelog would leave a first-time reader unaware that the
            // other two kinds exist at the one moment they are deciding what to make.
            body: canAuthor
              ? "An update is four choices: what it publishes, the repositories to watch, how often to look, and where to publish. A changelog is written from the commits; an executive summary or a security overview is relayed from that repository's own EXECUTIVE.md or SECURITY.md, one repository apiece. The wording and format are fixed whichever you pick, so there is nothing else to decide."
              : "An update runs on a cadence and publishes to Confluence: a changelog written from the commits, or an executive summary or security overview relayed from the repository's own document. Creating one needs the job-author role in this business unit; an administrator grants it through a group.",
            ...(createButton === undefined ? {} : { action: createButton }),
          }}
        />
      ) : null}
    </Page>
  );
}
